import React, { useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import Layer from '../../../../../Shared/Layer'
import { saveAndConfirmImages } from '../../../../../../api'
import activityTrackingService from '../../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../../constants'
import './styles.scss'
import Button from '../../../../../Shared/Form/Button'
import Checkbox from '../../../../../Shared/Form/Checkbox'
import Alert from '../../../../../Shared/Alert'
import Loader from '../../../../../Shared/Loader'
import ImagesConfirmedSuccessfullyOverlay from '../ImagesConfirmedSuccessfullyOverlay'

const initialState = {
  loading: false,
  checkboxAgree: false,
  error: false,
  step: 1,
}

const LayerConfirmImages = React.memo((props) => {
  const [loading, setLoading] = useState(initialState.loading)
  const [checkboxAgree, setCheckboxAgree] = useState(initialState.checkboxAgree)
  const [error, setError] = useState(initialState.error)
  const [step, setStep] = useState(initialState.step)

  const layerRef = useRef()
  const { t } = useTranslation()
  const onAgreeChange = useCallback((e) => {
    setCheckboxAgree(e.target.checked)
  }, [])

  const resetState = useCallback(() => {
    setLoading(initialState.loading)
    setCheckboxAgree(initialState.checkboxAgree)
    setError(initialState.error)
    setStep(initialState.step)
  }, [])

  const onHideClick = useCallback(() => {
    resetState()
    props.onHideClick()
  }, [props, resetState])

  const onCancel = useCallback(() => {
    if (layerRef.current) {
      onHideClick()
    }
  }, [onHideClick])

  const onConfirm = useCallback(() => {
    activityTrackingService.trackEvent(trackingEvent.saveImages, {
      giataId: props.images[0].giataId,
    })

    setLoading(true)
    setError(null)

    const { fetchPromise } = saveAndConfirmImages(props.images[0].giataId)

    fetchPromise
      .then(() => {
        setLoading(false)
        setStep(2)
        props.onConfirmed()
      })
      .catch(() => {
        setLoading(false)
        setError(true)
      })
  }, [props])

  if (!props.show) return null
  if (step === 2) {
    return <ImagesConfirmedSuccessfullyOverlay onCancel={onHideClick}/>
  }

  return (
    <Layer
      disallowHiding={loading}
      ref={layerRef}
      type="center"
      size="small"
      id="images_confirm_layer"
      show={true}
      showCloseIcon={step === 2}
      onHideClick={onHideClick}
    >
      <div className={'layer-inner-container-wrapper'}>
        <div className={'layer-inner-container'}>
          <>
            {loading && <Loader size={64}/>}
            <div className={'layer-header'}>
              <div className={'layer-header-title'}>{t('CONFIRM_IMAGES')}</div>
              <div className={'layer-header-title-sub'}>
                Please read the terms and conditions and accept them to proceed.
              </div>
              <hr/>
            </div>
            <div className={'layer-content layer-content-scrollable'}>
              {error && (
                <Alert type="danger">
                  Sorry, there was a problem. Please try again in a few minutes.
                </Alert>
              )}
              <div style={{ whiteSpace: 'pre-line' }}>{t('IMAGE_CONFIRM_AGREEMENT_TEXT')}</div>
              <div className={'layer-content-actions'}>
                <div className={'layer-content-action-agree'}>
                  <Checkbox value={checkboxAgree} onChange={onAgreeChange}/>
                  <span>{t('I_HAVE_READ_AND_AGREE')}</span>
                </div>
              </div>
            </div>
            <div className={'layer-footer'}>
              <div>
                <Button
                  align="right"
                  disabled={!(checkboxAgree)}
                  onClick={onConfirm}
                >{t('BUTTON_CONFIRM')}
                </Button>
                <Button onClick={onCancel} variant="neutral">{t('BUTTON_CANCEL')}</Button>
              </div>
            </div>
          </>
        </div>
      </div>
    </Layer>
  )
})

LayerConfirmImages.propTypes = {
  images: PropTypes.array.isRequired,
  onConfirmed: PropTypes.func.isRequired,
  user: PropTypes.string.isRequired,
  onHideClick: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
}

export default LayerConfirmImages
